import React from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import {
  ClockModeState,
  componentThemeAtom,
  ComponentTheme,
  clockModeThemeAtom,
  clockModeVisualConfigAtom,
  ComponentMaterial,
  customBackgroundImageAtom,
} from "../../../../atoms/v3/clockModeAtoms";
import ClockModeIcon from "./ClockModeIcon";
import ClockSettingsModal from "./components/ClockSettingsModal";
import { ClockModeThemeSetting, ClockModeThemeSettings } from "./themes/ClockModeThemeSettings";
import CustomBackgroundUploader from "./components/CustomBackgroundUploader";
import { Image } from "lucide-react";

interface ClockModeControlsProps {
  state: ClockModeState;
  onStateUpdate: (updates: Partial<ClockModeState>) => void;
  onExit: () => void;
  onEnterConfigMode?: () => void;
  // showAllControls?: boolean;
}

/**
 * 时钟模式控制按钮组件
 * 包含横竖屏、常亮、换肤、退出按钮
 */
export const ClockModeControls: React.FC<ClockModeControlsProps> = ({
  state,
  onStateUpdate,
  onExit,
  onEnterConfigMode,
  // showAllControls = false,
}) => {
  const { t } = useTranslation(["v3/clock"]);
  const [componentTheme, setComponentTheme] = useAtom(componentThemeAtom);
  const [clockTheme, setClockTheme] = useAtom(clockModeThemeAtom);
  const [themeConfig, setThemeConfig] = useAtom(clockModeVisualConfigAtom);
  const [customBackgroundImage] = useAtom(customBackgroundImageAtom);
  const [showAllControls, setShowAllControls] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [showSettings, setShowSettings] = React.useState(false);

  // 点击外部关闭菜单
  React.useEffect(() => {
    if (!showAllControls) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowAllControls(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAllControls]);

  // 切换强制旋转
  const toggleForceRotation = () => {
    const rotations: Array<"none" | "rotate-90" | "rotate-180" | "rotate-270"> =
      ["none", "rotate-90", "rotate-180", "rotate-270"];
    const currentIndex = rotations.indexOf(state.forceRotation);
    const nextIndex = (currentIndex + 1) % rotations.length;
    onStateUpdate({ forceRotation: rotations[nextIndex] });
  };

  // 切换屏幕常亮
  const toggleKeepScreenOn = () => {
    // 检测浏览器是否支持屏幕常亮
    if (!("wakeLock" in navigator)) {
      alert("您的浏览器不支持屏幕常亮功能");
      return;
    }

    // 切换状态
    onStateUpdate({ keepScreenOn: !state.keepScreenOn });

    // 显示状态提示
    if (!state.keepScreenOn) {
      alert("屏幕常亮已开启并保存偏好，如有适配问题请在内测群反馈");
    }
  };

  // 切换时钟主题（只有 light）
  const toggleClockTheme = () => {
    alert("当前只有一个主题可用");
  };

  // 切换组件主题
  const toggleComponentTheme = () => {
    const themes: ComponentTheme[] = ["light", "dark"];
    const currentIndex = themes.indexOf(componentTheme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setComponentTheme(themes[nextIndex]);
  };

  const handleVisualSettingsClick = () => {
    setShowSettings(true);
    setShowAllControls(false);
  };

  const handleSetClockTheme = (theme: ClockModeThemeSetting) => {
    setClockTheme(theme.id);
    setComponentTheme(theme.type);
  };

  // 按钮配置
  interface ControlButton {
    id: string;
    onClick: () => void;
    icon: string;
    labelKey: string;
    titleKey?: string;
    visible?: boolean;
  }

  const controlButtons: ControlButton[] = [
    {
      id: "exit",
      onClick: onExit,
      icon: "arrow-back",
      labelKey: "v3/clock:controls.exit",
    },
    // {
    //   id: 'rotation',
    //   onClick: toggleForceRotation,
    //   icon: <span className="w-3 h-3">{getRotationIcon()}</span>,
    //   labelKey: 'v3/clock:controls.rotation',
    //   titleKey: `${t('v3/clock:controls.rotation', '旋转')}: ${
    //     state.forceRotation === 'none' ? t('v3/clock:controls.rotationNone', '无') :
    //     state.forceRotation === 'rotate-90' ? t('v3/clock:controls.rotation90', '90°') :
    //     state.forceRotation === 'rotate-180' ? t('v3/clock:controls.rotation180', '180°') :
    //     t('v3/clock:controls.rotation270', '270°')
    //   }`,
    // },
    // {
    //   id: 'settings',
    //   onClick: onEnterConfigMode || (() => {}),
    //   icon: getSettingsIcon(),
    //   labelKey: 'v3/clock:controls.settings',
    //   visible: !!onEnterConfigMode,
    // },
    // {
    //   id: "refresh",
    //   onClick: () => window.location.reload(),
    //   icon: "play",
    //   labelKey: "v3/clock:controls.refresh",
    //   titleKey: t("v3/clock:controls.refresh", "刷新页面"),
    // },
    {
      id: "keepScreenOn",
      onClick: toggleKeepScreenOn,
      icon: state.keepScreenOn ? "sun-high" : "sun-off",
      labelKey: "v3/clock:controls.keepScreenOn",
      titleKey: `${t("v3/clock:controls.keepScreenOn", "屏幕常亮")}: ${
        state.keepScreenOn
          ? t("v3/clock:controls.on", "开启")
          : t("v3/clock:controls.off", "关闭")
      }`,
    },
    {
      id: "clockTheme",
      onClick: toggleClockTheme,
      icon: "clock",
      labelKey: "v3/clock:controls.clockTheme",
      titleKey: `${t("v3/clock:controls.clockTheme", "时钟主题")}: ${state.theme}`,
      visible: false, // 隐藏，因为只有一个主题
    },
    {
      id: "componentTheme",
      onClick: toggleComponentTheme,
      icon: "settings",
      labelKey: "v3/clock:controls.darkMode",
      titleKey: `${t("v3/clock:controls.darkMode", "时钟深色模式")}: ${
        componentTheme === "dark"
          ? t("v3/clock:controls.on", "开启")
          : t("v3/clock:controls.off", "关闭")
      }`,
      visible: false,
    },
    {
      id: "visualSettings",
      onClick: handleVisualSettingsClick,
      icon: "settings",
      labelKey: "v3/clock:controls.visualSettings",
      titleKey: `${t("v3/clock:controls.visualSettings", "外观设置")}
      }`,
    },
  ];

  // 处理按钮点击 - 简化为直接执行功能
  const handleButtonClick = (button: ControlButton) => {
    button.onClick();
  };

  // 通用按钮组件
  const ControlButton: React.FC<{
    button: ControlButton;
    isExitButton?: boolean;
  }> = ({ button, isExitButton = false }) => {
    // 判断按钮是否处于激活状态
    const isActive =
      (button.id === "keepScreenOn" && state.keepScreenOn) ||
      (button.id === "componentTheme" && componentTheme === "dark");

    return (
      <button
        onClick={() => handleButtonClick(button)}
        className={`clock-menu-button ${isActive ? "active" : ""}`}
        title={button.titleKey || t(button.labelKey)}
      >
        <div className="flex items-center space-x-1 min-w-[160px]">
          <span className="grow text-left">{t(button.labelKey)}</span>
          <ClockModeIcon type={button.icon} size={16} />
        </div>
      </button>
    );
  };

  // 获取要显示的按钮
  const getVisibleButtons = () => {
    const filteredButtons = controlButtons.filter(
      (button) => button.visible !== false
    );

    if (!showAllControls) {
      // 只显示 exit 按钮
      return filteredButtons.filter((button) => button.id === "exit");
    }

    // 悬停时显示所有按钮
    return filteredButtons;
  };

  return (
    <>
      <div className="absolute top-4 left-4 z-999" ref={menuRef}>
        <button
          className={`clock-menu-toggle-button ${showAllControls ? "active" : ""}`}
          onClick={() => setShowAllControls(!showAllControls)}
        >
          <ClockModeIcon type="menu" size={16} />
        </button>
        {/* 控制按钮组 - 垂直排列避免换行 */}
        {showAllControls && (
          <div className="clock-menu">
            {getVisibleButtons().map((button) => (
              <ControlButton
                key={button.id}
                button={button}
                isExitButton={button.id === "exit"}
              />
            ))}
          </div>
        )}
      </div>

      {showSettings && (
        <ClockSettingsModal
          show={showSettings}
          setShow={setShowSettings}
          header={"外观设置"}
        >
          <div className="inner">
            <span className="tips">{"个性化定制您的时钟模式。"}</span>
            <div className="spacer"></div>

            <span className="settings-header">{"主题"}</span>
            <div className="clock-theme-list">
              {ClockModeThemeSettings.map((theme) => (
                <button
                  key={theme.label}
                  onClick={() => handleSetClockTheme(theme)}
                  className={`clock-theme-button ${
                    clockTheme === theme.id ? "active" : ""
                  }`}
                >
                  <div
                    className="clock-theme-swatch"
                    style={
                      theme.isCustom
                        ? {
                            background: customBackgroundImage
                              ? `url(${customBackgroundImage})`
                              : "linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }
                        : { background: theme.value, backgroundSize: "cover" }
                    }
                  >
                    {theme.isCustom && !customBackgroundImage && (
                      <Image size={16} className="text-gray-400" />
                    )}
                  </div>
                  <span>{theme.label}</span>
                </button>
              ))}
            </div>

            {/* 自定义背景上传区域 - 仅在选中自定义图片主题时显示 */}
            {clockTheme === "custom-image" && (
              <>
                <div className="spacer"></div>
                <span className="settings-header">{"自定义背景图片"}</span>
                <div className="custom-background-upload-area">
                  <CustomBackgroundUploader
                    onUploadComplete={() => {
                      // 上传完成后可以添加一些反馈
                      console.log("Background uploaded successfully");
                    }}
                  />
                </div>
              </>
            )}

            <div className="spacer"></div>
            <span className="settings-header">{"小组件材质"}</span>
            <div className="option-list">
              {[
                {
                  value: "mica",
                  label: "雾化云母",
                  description: "柔润优雅，光在其中慢慢流动",
                },
                {
                  value: "glass",
                  label: "透盈玻璃",
                  description: "晶莹透亮，一如闪闪发光的你",
                },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={
                    () => {
                      setThemeConfig((prev) => ({
                        ...prev,
                        componentMaterial: option.value as ComponentMaterial,
                      }));
                    }
                  }
                  className={`option-button ${
                    themeConfig.componentMaterial === option.value
                      ? "active"
                      : ""
                  }`}
                >
                  <span>{option.label}</span>
                  <span className="opacity-35 py-[6px] truncate">
                    - {option.description}
                  </span>
                </button>
              ))}
            </div>

            <div className="spacer"></div>
            <span className="settings-header">{"背景模糊"}</span>
            <div className="option-list">
              {[
                {
                  value: false,
                  label: "关闭",
                  description: "保持背景清晰锐利，并节约部分系统资源",
                },
                {
                  value: true,
                  label: "开启",
                  description: "开启背景模糊",
                },
              ].map((option) => (
                <button
                  key={option.label}
                  onClick={
                    () => {
                      setThemeConfig((prev) => ({
                        ...prev,
                        useBackgroundBlur: option.value,
                      }));
                    }
                  }
                  className={`option-button ${
                    themeConfig.useBackgroundBlur === option.value
                      ? "active"
                      : ""
                  }`}
                >
                  <span>{option.label}</span>
                  <span className="opacity-35 py-[6px] truncate">
                    - {option.description}
                  </span>
                </button>
              ))}
            </div>

            <div className="inline-block"></div>
          </div>
        </ClockSettingsModal>
      )}
    </>
  );
};

export default ClockModeControls;
